import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { rubroVisible } from "../lib/onboardingPendiente";

/**
 * Una pantalla con trabajo sin guardar puede pedir que se le pregunte antes de cambiar de
 * marca: el cambio desmonta lo que dependa de la marca activa y ese trabajo se perdía sin
 * aviso (review Greptile #169, ADR-2026-10-08-cambiar-de-marca-pregunta-si-hay-cambios).
 * Devuelve `true` para seguir con el cambio y `false` para cancelarlo. Una sola a la vez: la
 * pantalla que la registra la saca al desmontarse.
 */
type ConfirmarCambioDeMarca = () => Promise<boolean>;
let confirmarCambioDeMarca: ConfirmarCambioDeMarca | null = null;

export function registrarConfirmacionDeCambioDeMarca(fn: ConfirmarCambioDeMarca | null): void {
  confirmarCambioDeMarca = fn;
}

/**
 * Lo que llama CADA camino que cambia de marca antes de hacerlo (este hook y el menú del
 * teléfono en `Sidebar`, que tiene su propio `switchBrand`). `true` si no hay nada registrado.
 */
export function confirmarAntesDeCambiarDeMarca(): Promise<boolean> {
  return confirmarCambioDeMarca ? confirmarCambioDeMarca() : Promise.resolve(true);
}

export interface BrandRow {
  id: string;
  name: string;
  /** Rubro declarado para ESTA marca (`brand_profiles.rubro`). */
  rubro: string | null;
  logo_url: string | null;
  is_default: boolean;
}

export interface BrandProfile {
  id: string;
  name: string | null;
  id_company: string | null;
  company_name: string | null;
  company_logo: string | null;
  /**
   * Rubro a nivel empresa (`company_profiles.rubro`). Es el fallback cuando la
   * marca activa no tiene el suyo — el mismo orden que usa el editor de marca
   * (`config_sections/CompanyData`), para que no haya dos rubros distintos según
   * qué pantalla se mire.
   */
  rubro: string | null;
  plan: string | null;
  imagenes_restantes: number | null;
  max_fotos_mes: number | null;
}

/**
 * Carga el perfil del usuario + sus créditos + sus marcas (brand_profiles) y
 * permite cambiar de marca activa. Espeja la lógica del Sidebar pero como hook
 * reutilizable. Se mantiene en sync con el resto de la app vía los eventos
 * `atlas:brand-changed` y `atlas:credits-updated`.
 */
export function useBrandProfile() {
  const [profile, setProfile] = useState<BrandProfile | null>(null);
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [currentBrandId, setCurrentBrandId] = useState<string | null>(null);
  const [switching, setSwitching] = useState(false);
  const [loading, setLoading] = useState(true);
  /**
   * `true` mientras `fetchBrands` está en vuelo. Aditivo — antes no existía
   * ninguna señal para distinguir "todavía no se pidieron las marcas de esta
   * empresa" de "se pidieron y no hay ninguna". Sin esto, un consumidor que
   * espera a `loading === false` para decidir "esta cuenta no tiene marca"
   * se equivoca: `loading` solo cubre el perfil, no el segundo paso
   * (`fetchBrands`, que corre en un efecto aparte una vez que se conoce
   * `id_company`) — ver ADR-2026-08-16-marca-page-lectura.
   *
   * Convive con `marcasResueltas` (abajo) y NO es lo mismo: aquél se latchea en
   * `true` para siempre, éste vuelve a `true` en CADA refetch. `MarcaPage` y
   * `BannerGuardado` dependen justamente de ese ciclo para no pintar datos
   * viejos después de un `atlas:brand-changed`.
   */
  const [loadingBrands, setLoadingBrands] = useState(false);
  /**
   * `true` cuando `fetchBrands` ya corrió al menos una vez —
   * ADR-2026-08-04-cargando-tambien-cubre-las-marcas.
   *
   * Son DOS fetch encadenados (perfil → marcas) y `loading` cubría sólo el primero,
   * así que quedaba una ventana con `loading === false` y `brands` todavía vacío. En
   * esa ventana `activeBrand` es `null` y quien haga `activeBrand?.rubro ?? profile.rubro`
   * lee el rubro de la EMPRESA creyendo que es el de la marca activa. Con un consumidor
   * que sólo pinta distinto es un parpadeo; con uno que DECIDE —el guard de `/plantillas`
   * redirige a la home— es un rebote irreversible.
   *
   * Nunca vuelve a `false`: los refetch por `atlas:brand-changed` son silenciosos y no
   * tienen que reabrir el esqueleto de nadie.
   */
  const [marcasResueltas, setMarcasResueltas] = useState(false);

  const fetchProfile = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const { data: prof } = await supabase
        .from("profiles")
        .select("id, name, id_company")
        .eq("id", user.id)
        .single();
      if (!prof) return;
      if (prof.id_company) {
        const { data: company } = await supabase
          .from("company_profiles")
          .select("nombre, logo_url, rubro, plan, max_fotos_mes, imagenes_restantes:imagenes_disponibles")
          .eq("id_company", prof.id_company)
          .single();
        setProfile({
          id: prof.id,
          name: prof.name,
          id_company: prof.id_company,
          company_name: company?.nombre ?? null,
          company_logo: company?.logo_url ?? null,
          // El centinela del alta por link no sale de este hook: acá se convierte en
          // "no hay rubro", que es lo que en realidad significa (`lib/onboardingPendiente`).
          rubro: rubroVisible(company?.rubro),
          plan: company?.plan ?? null,
          imagenes_restantes: company?.imagenes_restantes ?? null,
          max_fotos_mes: company?.max_fotos_mes ?? null,
        });
      } else {
        setProfile({
          id: prof.id,
          name: prof.name,
          id_company: null,
          company_name: null,
          company_logo: null,
          rubro: null,
          plan: null,
          imagenes_restantes: null,
          max_fotos_mes: null,
        });
      }
    } catch {
      /* noop */
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const fetchBrands = useCallback(async (companyId: string) => {
    setLoadingBrands(true);
    try {
      const { data: brandsData } = await supabase
        .from("brand_profiles")
        .select("id, name, rubro, logo_url, is_default")
        .eq("id_company", companyId)
        .eq("is_draft", false)
        .order("is_default", { ascending: false })
        .order("created_at", { ascending: true });
      // Mismo saneo que el rubro de la empresa, y por la misma razón: la marca del alta
      // por link nace con el centinela y esta lista es la que pintan la tarjeta de la
      // sidebar, su menú y el selector mobile.
      const list = ((brandsData as BrandRow[] | null) ?? []).map((b) => ({
        ...b,
        rubro: rubroVisible(b.rubro),
      }));
      setBrands(list);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profData } = await supabase
        .from("profiles")
        .select("current_brand_id")
        .eq("id", user.id)
        .single();
      const fromProfile = (profData as { current_brand_id?: string | null })
        ?.current_brand_id as string | null;
      const fallback =
        list.find((b) => b.is_default)?.id ?? list[0]?.id ?? null;
      setCurrentBrandId(fromProfile ?? fallback);
    } catch {
      /* noop */
    } finally {
      // En `finally` y no al final del `try`: si la consulta falla, la marca activa
      // queda en `null` para siempre y dejar a los consumidores esperando eternamente
      // es peor que dejarlos decidir con el fallback de empresa.
      setLoadingBrands(false);
      setMarcasResueltas(true);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (profile?.id_company) fetchBrands(profile.id_company);
  }, [profile?.id_company, fetchBrands]);

  // Sync con el resto de la app.
  useEffect(() => {
    const onBrand = () => {
      if (profile?.id_company) fetchBrands(profile.id_company);
      // También el perfil de empresa, y no sólo las marcas — ADR-2026-07-31-editar-nombre-rubro-marca-refleja. El evento ya
      // no significa únicamente "cambió la marca activa": desde que las ediciones de
      // nombre y rubro lo emiten, puede haber cambiado `company_profiles`, que es de
      // donde salen los fallbacks (`company_name`, `rubro`) y el único lugar que se
      // escribe cuando la cuenta no tiene marcas. Va en `silent` para no encender el
      // `loading` y hacer parpadear a los consumidores.
      fetchProfile(true);
    };
    const onCredits = () => fetchProfile(true);
    window.addEventListener("atlas:brand-changed", onBrand);
    window.addEventListener("atlas:credits-updated", onCredits);
    return () => {
      window.removeEventListener("atlas:brand-changed", onBrand);
      window.removeEventListener("atlas:credits-updated", onCredits);
    };
  }, [profile?.id_company, fetchBrands, fetchProfile]);

  const switchBrand = useCallback(
    async (brandId: string) => {
      if (brandId === currentBrandId || switching) return;
      if (!(await confirmarAntesDeCambiarDeMarca())) return;
      setSwitching(true);
      const prev = currentBrandId;
      setCurrentBrandId(brandId); // optimista
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error("no user");
        const { error } = await supabase
          .from("profiles")
          .update({ current_brand_id: brandId })
          .eq("id", user.id);
        if (error) throw error;
        window.dispatchEvent(
          new CustomEvent("atlas:brand-changed", { detail: { brandId } }),
        );
      } catch {
        setCurrentBrandId(prev); // rollback
      } finally {
        setSwitching(false);
      }
    },
    [currentBrandId, switching],
  );

  const activeBrand =
    brands.find((b) => b.id === currentBrandId) ??
    brands.find((b) => b.is_default) ??
    brands[0] ??
    null;

  return {
    profile,
    brands,
    currentBrandId,
    activeBrand,
    switchBrand,
    switching,
    /**
     * Cubre las DOS cargas: el perfil y las marcas de la cuenta
     * (ADR-2026-08-04-cargando-tambien-cubre-las-marcas). Mientras sea `true`,
     * `activeBrand` todavía puede ser `null` teniendo la cuenta marcas — o sea que
     * NADA que dependa de la marca activa (su rubro, su logo, su id) está resuelto.
     *
     * Sin `id_company` no hay marcas que esperar y alcanza con el perfil.
     */
    loading: loading || (profile?.id_company ? !marcasResueltas : false),
    loadingBrands,
    refresh: fetchProfile,
  };
}
